import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Search, Filter, AlertCircle, CheckCircle2,
  Clock, SkipForward, XCircle, ChevronRight, X, Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_TONE = {
  completed: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  running:   'bg-amber-500/15  text-amber-200  border-amber-500/30',
  failed:    'bg-rose-500/15   text-rose-200   border-rose-500/30',
  skipped:   'bg-zinc-500/15   text-zinc-300   border-zinc-500/30',
  pending:   'bg-blue-500/15   text-blue-200   border-blue-500/30',
  exited:    'bg-slate-500/15  text-slate-300  border-slate-500/30',
};
const StatusBadge = ({ status }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-[11px] border ${STATUS_TONE[status] || 'bg-slate-500/15 text-slate-300 border-slate-500/30'}`}
    data-testid={`exec-status-${status || 'unknown'}`}
  >
    {status || '—'}
  </span>
);

const fmtTs = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const truncate = (s, n = 80) => {
  if (!s) return '—';
  const t = String(s);
  return t.length > n ? t.slice(0, n) + '…' : t;
};

export default function JourneyExecutionsPanel({ journey, onClose }) {
  const journeyId = journey?.id;
  const [filters, setFilters] = useState({
    status: 'all', days: 30, q: '', has_errors: false,
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState(null);
  const limit = 100;

  const load = useCallback(async () => {
    if (!journeyId) return;
    setLoading(true);
    try {
      const params = {
        days: filters.days,
        limit, skip,
      };
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.has_errors) params.has_errors = true;
      const r = await axios.get(`${API}/journeys/${journeyId}/executions`, {
        headers: authHeaders(), params,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load executions');
    } finally { setLoading(false); }
  }, [journeyId, filters, skip]);

  useEffect(() => { load(); }, [load]);

  const failedCount = useMemo(
    () => items.filter(e => (e.error_count || 0) > 0).length,
    [items],
  );

  return (
    <div className="flex flex-col w-[520px] xl:w-[640px] border-l border-wt-border bg-wt-bg/40 overflow-hidden" data-testid="executions-panel">
      <div className="p-4 border-b border-wt-border flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-wt-text-muted">Journey Executions</div>
          <div className="text-sm font-semibold text-wt-text-primary">{journey?.name || '—'}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={load} data-testid="executions-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="executions-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-3 border-b border-wt-border space-y-2">
        <div className="flex gap-2 flex-wrap">
          <Select value={filters.status} onValueChange={(v) => { setSkip(0); setFilters(f => ({ ...f, status: v })); }}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="exec-filter-status">
              <Filter className="w-3 h-3 mr-1" /> <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="exited">Exited</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(filters.days)} onValueChange={(v) => { setSkip(0); setFilters(f => ({ ...f, days: Number(v) })); }}>
            <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="exec-filter-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24h</SelectItem>
              <SelectItem value="7">Last 7d</SelectItem>
              <SelectItem value="30">Last 30d</SelectItem>
              <SelectItem value="90">Last 90d</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-2 w-3 h-3 text-wt-text-muted" />
            <Input
              placeholder="Phone, email, order #, name…"
              value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); load(); } }}
              className="h-8 pl-7 text-xs"
              data-testid="exec-filter-search"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-wt-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={filters.has_errors}
            onChange={(e) => { setSkip(0); setFilters(f => ({ ...f, has_errors: e.target.checked })); }}
            data-testid="exec-filter-has-errors"
          />
          Only show rows with at least one failed node
        </label>
        <div className="text-[11px] text-wt-text-muted flex items-center gap-3">
          <span>{total} total</span>
          {failedCount > 0 && (
            <span className="text-rose-300">{failedCount} with failures on this page</span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading && items.length === 0 ? (
          <div className="p-6 text-center text-wt-text-muted text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-wt-text-muted text-sm">No executions for the selected filters.</div>
        ) : (
          <ul className="divide-y divide-wt-border" data-testid="executions-table">
            {items.map(ex => {
              const hasErrors = (ex.error_count || 0) > 0;
              return (
                <li
                  key={ex.id}
                  className="p-3 hover:bg-wt-bg/60 cursor-pointer"
                  onClick={() => setSelected(ex.id)}
                  data-testid={`execution-row-${ex.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge status={ex.status} />
                        {hasErrors && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-rose-500/15 text-rose-200 border border-rose-500/30" data-testid={`exec-error-count-${ex.id}`}>
                            <AlertCircle className="w-3 h-3" /> {ex.error_count} failed node{ex.error_count > 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="text-[10px] font-mono text-wt-text-muted">
                          {fmtTs(ex.entered_at || ex.started_at)}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-wt-text-primary truncate">
                        {ex.order_number ? (
                          <span className="font-mono text-wt-gold">#{ex.order_number}</span>
                        ) : null}
                        {ex.order_number && (ex.customer_name || ex.phone || ex.email) ? ' · ' : ''}
                        {ex.customer_name || ex.phone || ex.email || ex.entity_id || '—'}
                      </div>
                      {hasErrors && ex.last_error && (
                        <div className="mt-1 text-[11px] text-rose-300 truncate" data-testid={`exec-last-error-${ex.id}`}>
                          ❌ {ex.last_failed_action ? `${ex.last_failed_action}: ` : ''}{truncate(ex.last_error, 100)}
                        </div>
                      )}
                      <div className="mt-1 text-[11px] text-wt-text-muted">
                        {ex.node_count || 0} node{ex.node_count === 1 ? '' : 's'} run · current: {ex.current_node_id || '—'}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-wt-text-muted mt-1 flex-none" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pager */}
      {total > limit && (
        <div className="p-3 border-t border-wt-border flex items-center justify-between text-xs">
          <span className="text-wt-text-muted">{skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Prev</Button>
            <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Next</Button>
          </div>
        </div>
      )}

      {selected && (
        <ExecutionDetailModal
          journeyId={journeyId}
          executionId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

const NODE_ICON = {
  completed: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />,
  failed:    <XCircle      className="w-3.5 h-3.5 text-rose-400" />,
  skipped:   <SkipForward  className="w-3.5 h-3.5 text-zinc-400" />,
  running:   <Clock        className="w-3.5 h-3.5 text-amber-400 animate-pulse" />,
  pending:   <Clock        className="w-3.5 h-3.5 text-blue-400" />,
};

const ExecutionDetailModal = ({ journeyId, executionId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/journeys/${journeyId}/executions/${executionId}`, { headers: authHeaders() })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load detail'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [journeyId, executionId]);

  const ex = data?.execution || {};
  const logs = ex.execution_log || [];
  const customer = data?.customer || null;

  const copyJson = (obj) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="execution-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Execution detail</span>
            <StatusBadge status={ex.status} />
            <span className="font-mono text-[11px] text-wt-text-muted">{executionId.slice(0, 8)}…</span>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => copyJson(ex)} data-testid="copy-execution-json-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy JSON
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-wt-text-muted text-sm">Loading…</div>
        ) : (
          <div className="space-y-5 text-sm">
            {/* Customer card */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-wt-paper/50 rounded p-3 border border-wt-border">
              <Field label="Customer" value={customer?.full_name || `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() || ex.phone || '—'} />
              <Field label="Email" value={customer?.email || '—'} />
              <Field label="Phone" value={customer?.phone || ex.phone || '—'} />
              <Field label="Shopify ID" value={customer?.shopify_customer_id || '—'} />
              <Field label="Entered" value={fmtTs(ex.entered_at || ex.started_at)} />
              <Field label="Completed" value={fmtTs(ex.completed_at) || '—'} />
              <Field label="Current node" value={ex.current_node_id || '—'} />
              <Field label="Local credit balance" value={customer?.credit_balance != null ? String(customer.credit_balance) : '—'} />
            </div>

            {/* Timeline */}
            <div>
              <div className="text-xs uppercase text-wt-text-muted mb-2">Node Timeline ({logs.length})</div>
              {logs.length === 0 ? (
                <p className="text-xs text-wt-text-muted">No node logs yet.</p>
              ) : (
                <ol className="space-y-2" data-testid="execution-timeline">
                  {logs.map((n, idx) => {
                    const k = n.node_id || `node-${idx}`;
                    const isExpanded = !!expanded[k];
                    const tone = n.status === 'failed'
                      ? 'border-rose-500/40 bg-rose-500/5'
                      : n.status === 'completed'
                        ? 'border-emerald-500/30 bg-emerald-500/5'
                        : 'border-wt-border';
                    return (
                      <li
                        key={`${k}-${idx}`}
                        className={`border rounded ${tone}`}
                        data-testid={`timeline-node-${n.status}-${idx}`}
                      >
                        <button
                          className="w-full px-3 py-2 flex items-center gap-2 text-left"
                          onClick={() => setExpanded(s => ({ ...s, [k]: !s[k] }))}
                        >
                          {NODE_ICON[n.status] || <Clock className="w-3.5 h-3.5 text-slate-400" />}
                          <span className="text-sm text-wt-text-primary">
                            {n.node_label || n.action || 'Node'}
                          </span>
                          <span className="text-[10px] font-mono text-wt-text-muted ml-auto">
                            {fmtTs(n.timestamp)}
                          </span>
                          <StatusBadge status={n.status} />
                        </button>
                        {n.status === 'failed' && n.error && !isExpanded && (
                          <div className="px-9 pb-2 text-[11px] text-rose-300" data-testid={`node-error-${idx}`}>
                            {truncate(n.error, 180)}
                          </div>
                        )}
                        {isExpanded && (
                          <div className="px-3 pb-3 space-y-2 border-t border-wt-border/60">
                            {n.error && (
                              <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-[11px] text-rose-200">
                                <b>Error:</b> {n.error}
                              </div>
                            )}
                            {n.details && Object.keys(n.details).length > 0 && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="text-[10px] uppercase text-wt-text-muted">Details</div>
                                  <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => copyJson(n.details)}>
                                    <Copy className="w-3 h-3 mr-1" /> Copy
                                  </Button>
                                </div>
                                <pre className="text-[10px] bg-wt-bg/70 border border-wt-border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                                  {JSON.stringify(n.details, null, 2)}
                                </pre>
                              </div>
                            )}
                            <div className="text-[10px] text-wt-text-muted">
                              node_id: <span className="font-mono">{n.node_id || '—'}</span>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* Entity data snapshot */}
            <details className="border border-wt-border rounded">
              <summary className="cursor-pointer px-3 py-2 text-xs text-wt-text-secondary hover:bg-wt-bg/60" data-testid="entity-data-toggle">
                Entity data snapshot ({Object.keys(ex.entity_data || {}).length} keys)
              </summary>
              <pre className="text-[10px] bg-wt-bg/70 p-3 overflow-x-auto whitespace-pre-wrap break-all border-t border-wt-border">
                {JSON.stringify(ex.entity_data || {}, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase text-wt-text-muted">{label}</div>
    <div className="text-wt-text-primary truncate" title={value}>{value || '—'}</div>
  </div>
);
